import React, { useEffect, useState } from "react";
import {
  BottomSheet,
  Button,
  Modal,
  useIsMobile,
} from "../../../components/ui";
import type { BrandService } from "../types/brand.types";

/** The same two pills the card wears, so opening one changes nothing but size. */
const STATUS_STYLES: Record<BrandService["status"], string> = {
  Active: "bg-brand-soft text-[#00899B]",
  "In-active": "bg-[#F1F4F6] text-ink-muted",
};

interface ServiceDetailsModalProps {
  /** The service to show. `null` closes the dialog. */
  service: BrandService | null;
  /** True while a listing change is with the server; the buttons read it. */
  saving?: boolean;
  onClose: () => void;
  /** Hands the service to the form dialog. The tab swaps one for the other. */
  onEdit: (service: BrandService) => void;
  /** Confirmed, so this is the call. `active` is where it is going. */
  onSetActive: (service: BrandService, active: boolean) => void;
}

/**
 * One service, opened from its card: what it says, where its name came from,
 * and the two things that can be done to it.
 *
 * A dialog rather than a page because there is no more to a service than fits
 * in one — a name, a line, a goal and a date — and a route for four fields is
 * a back button between a practitioner and the grid they were reading.
 *
 * It confirms the listing change in place rather than opening a second dialog
 * over this one. Stacked modals fight over the same focus trap and the same
 * lock on the page behind them, and the question being asked here is about the
 * service already on screen — so the panel asks it where the service is, and
 * the answer is one press from where the question was raised.
 *
 * On a phone it is a sheet up from the bottom edge rather than a dialog in the
 * middle of the screen, the way a plan's card opens: the record arrives under
 * the thumb that pressed the card, with both buttons across it. The same
 * heading, body and footer go into whichever shell is drawn.
 */
const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  service,
  saving = false,
  onClose,
  onEdit,
  onSetActive,
}) => {
  // While set, the panel is asking rather than showing. Reset on every open,
  // so a dialog closed mid-question never reopens still asking it.
  const [confirming, setConfirming] = useState(false);

  // Which shell the record is shown in — see the note above.
  const isMobile = useIsMobile();

  useEffect(() => {
    if (service) setConfirming(false);
  }, [service]);

  // Read off the service rather than stored beside `confirming`: one flag
  // cannot then disagree with the card it came from.
  const deactivating = service?.status === "Active";

  const close = () => {
    if (saving) return;
    onClose();
  };

  /** The same two pills the card wears, so opening one changes nothing but
   *  size. Beside the name in the dialog's header; the sheet's heading is
   *  plain text, so there it is the first line of the body instead. */
  const pill = service && (
    <span
      className={`inline-flex flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
        STATUS_STYLES[service.status]
      }`}
    >
      {service.status}
    </span>
  );

  /** The two things that can be done to it — or, while asking, the answer
   *  and the way out. The same pair whichever shell is drawn. */
  const footer = confirming ? (
    <>
      <Button
        variant="secondary"
        size="md"
        onClick={() => setConfirming(false)}
        disabled={saving}
      >
        Cancel
      </Button>
      <Button
        variant={deactivating ? "danger" : "primary"}
        size="md"
        loading={saving}
        onClick={() => service && onSetActive(service, !deactivating)}
      >
        {deactivating ? "Deactivate" : "Activate"}
      </Button>
    </>
  ) : (
    <>
      <Button variant="secondary" size="md" onClick={() => setConfirming(true)}>
        {deactivating ? "Deactivate" : "Activate"}
      </Button>
      <Button size="md" onClick={() => service && onEdit(service)}>
        Edit
      </Button>
    </>
  );

  const body =
    service &&
    (confirming ? (
      <p className="text-[14px] 3xl:text-[15px] leading-[1.6] text-ink-muted">
        {deactivating
          ? "This service will stop showing on your public page until it is activated again. The plans priced against it are not touched."
          : "This service will show on your public page, so clients can see that you offer it."}
      </p>
    ) : (
      <dl className="flex flex-col gap-4">
        <Detail label="Description">
          {service.description || (
            <span className="text-ink-subtle">
              No description was written for this service.
            </span>
          )}
        </Detail>

        {/* Where the name came from. Worth saying, because it is the one
                thing about a service that an edit cannot change: a curated
                service stays attached to its goal however it is renamed. */}
        <Detail label="Goal">
          {service.goal ? (
            service.goal.name
          ) : (
            <span className="text-ink-subtle">
              Custom service — not linked to a goal.
            </span>
          )}
        </Detail>

        {service.createdOn && (
          <Detail label="Created on">{service.createdOn}</Detail>
        )}
      </dl>
    ));

  if (isMobile) {
    return (
      <BottomSheet
        open={!!service}
        title={service?.name ?? "Service details"}
        onClose={close}
        footer={footer}
      >
        <div className="flex flex-col gap-4 px-5 pb-2 pt-1">
          {/* Under the heading rather than beside it: the sheet's title is a
              string, and a pill has to be drawn somewhere. Left off while the
              sheet is asking — the question already says which way the
              service is going. */}
          {!confirming && <div>{pill}</div>}
          {body}
        </div>
      </BottomSheet>
    );
  }

  return (
    <Modal
      open={!!service}
      // Names the dialog to a screen reader, while `header` below draws the
      // heading — the name has a pill beside it, which a title cannot carry.
      title={service ? `${service.name} — service details` : "Service details"}
      hideTitle
      onClose={close}
      header={
        service && (
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-[17px] 3xl:text-[19px] font-medium text-ink">
              {service.name}
            </h2>
            <span className="mt-0.5 flex">{pill}</span>
          </div>
        )
      }
      footer={footer}
    >
      {body}
    </Modal>
  );
};

/** One labelled line of the record. */
const Detail: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <dt className="text-[13px] 3xl:text-[14px] text-ink-muted">{label}</dt>
    <dd className="mt-1 text-[14px] 3xl:text-[15px] leading-[1.6] text-ink">
      {children}
    </dd>
  </div>
);

export default ServiceDetailsModal;
